"use client";

// NettiQr — a real, camera-scannable QR code drawn in the sharer's own
// language: the three finder anchors wear the innernet CELL silhouette
// (concentric cell · paper gap · cell core, preserving the 1:1:3:1:1 read), the
// data modules soften into dots, the center carries the sharer's cell, and the
// whole code is inked in a tone of their hue on the ground the link opens in.
// Error correction buys the artistic liberties — a phone camera resolves it,
// and an AI reading a screenshot can follow the encoded URL straight to the
// memory (if that reader is allowed in).
//
// THE PAPER IS A GROUND, NOT A WHITE CHIP. `paper` is whatever the code is
// sitting in, so the quiet zone and the anchors' gaps carry it and the code
// belongs to the page instead of floating on a stamp. The caller owes it a
// module `ink` that survives that paper (lib/theme › inkRegime — a deep ground
// takes a LIGHT ink), because a QR is nothing but contrast.
//
// SMALLER MEANS SIMPLER. At a small size every module is a couple of device
// pixels, so the code has to carry less: `ecc` drops the correction level (Q
// still covers the cleared heart) and the modules are drawn fatter — fewer,
// heavier dots read where a dense H-level grid of thin ones smears.

import { useMemo, useRef } from "react";
import qrcode from "qrcode-generator";
import { CELL } from "../../lib/brand/cell";

/** One cell silhouette fitted (stretched) into a w×h box at (x, y). */
function CellShape({ x, y, w, h, fill }: { x: number; y: number; w: number; h: number; fill: string }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${w / 1071} ${h / 831})`}>
      <path d={CELL.path} fill={fill} />
    </g>
  );
}

export default function NettiQr({
  data,
  size = 232,
  ink,
  core,
  soft,
  paper = "#fffdf8",
  heart,
  ecc = "H",
  className,
}: {
  /** the encoded payload — the share URL. */
  data: string;
  size?: number;
  /** scannable module ink — must contrast with `paper` (law: inkRegime). */
  ink: string;
  /** the sharer's organism core color (the center cell gradient). */
  core: string;
  /** the sharer's softer secondary (gradient partner). */
  soft: string;
  /** the code's own ground: the quiet zone and the anchors' gaps. */
  paper?: string;
  /** a flat fill for the cell at the heart — white, the way THE button's disc
   *  is white on every ground. Omitted, it wears the organism gradient. */
  heart?: string;
  /** error correction. H is the most forgiving and the densest; Q reads better
   *  small, and still carries the cleared heart. */
  ecc?: "L" | "M" | "Q" | "H";
  className?: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);

  const model = useMemo(() => {
    const qr = qrcode(0, ecc);
    qr.addData(data);
    qr.make();
    const n = qr.getModuleCount();
    const inFinder = (r: number, c: number) =>
      (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
    // the center clearing for the sharer's cell — held inside the correction
    // level's own margin, so it shrinks when the level drops.
    const cx = n / 2;
    const cellW = Math.max(5, Math.round(n * (ecc === "H" ? 0.24 : 0.19)));
    const cellH = cellW / CELL.aspect;
    const clear = (r: number, c: number) =>
      Math.abs(c + 0.5 - cx) < cellW / 2 + 0.2 && Math.abs(r + 0.5 - cx) < cellH / 2 + 0.2;
    const dots: [number, number][] = [];
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (!qr.isDark(r, c) || inFinder(r, c) || clear(r, c)) continue;
        dots.push([c, r]);
      }
    }
    return { n, dots, cellW, cellH };
  }, [data, ecc]);

  const { n, dots, cellW, cellH } = model;
  const finders: [number, number][] = [
    [0, 0],
    [n - 7, 0],
    [0, n - 7],
  ];
  const gradId = useMemo(() => `nqr-${Math.abs(data.split("").reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) | 0, 7))}`, [data]);

  return (
    <svg
      ref={svgRef}
      className={className}
      viewBox={`-2 -2 ${n + 4} ${n + 4}`}
      width={size}
      height={size}
      role="img"
      aria-label="share code"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={soft} />
          <stop offset="1" stopColor={core} />
        </linearGradient>
      </defs>
      {/* quiet zone — the ground the code sits in, so it belongs to the page */}
      <rect x={-2} y={-2} width={n + 4} height={n + 4} rx={1.4} fill={paper} />
      {/* data modules as dots — fat enough to survive a small render */}
      {dots.map(([c, r]) => (
        <circle key={`${c}-${r}`} cx={c + 0.5} cy={r + 0.5} r={0.44} fill={ink} />
      ))}
      {/* the three anchors — concentric cells keeping the 1:1:3:1:1 structure */}
      {finders.map(([fc, fr]) => (
        <g key={`${fc}-${fr}`}>
          <CellShape x={fc} y={fr} w={7} h={7} fill={ink} />
          <CellShape x={fc + 1} y={fr + 1} w={5} h={5} fill={paper} />
          <CellShape x={fc + 2} y={fr + 2} w={3} h={3} fill={ink} />
        </g>
      ))}
      {/* the sharer's cell at the heart */}
      <CellShape x={n / 2 - cellW / 2} y={n / 2 - cellH / 2} w={cellW} h={cellH} fill={heart ?? `url(#${gradId})`} />
    </svg>
  );
}

/** Serialize a NettiQr SVG element into a downloadable .svg blob url. */
export function nettiQrDownload(svg: SVGSVGElement, filename: string) {
  const xml = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${xml}`], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
